import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useCallback, useEffect, useState } from 'react';

import {
  ActionIcon,
  Button,
  Group,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconMapPins,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
  IconX,
} from '@tabler/icons-react';

import { MedicalOrganization } from '@interfaces/medicalOrganizations.interface';
import {
  GetMedicalOrganizationsAddressesRequestParams,
  MedicalOrganizationAddress,
  MedicalOrganizationsAddressesSortBy,
} from '@interfaces/medicalOrganizationsAddresses.interface';

import { isApiError } from '@api/index';
import {
  useCountMedicalOrganizationsAddressesQuery,
  useDeleteMedicalOrganizationAddressMutation,
  useGetManyMedicalOrganizationsAddressesQuery,
} from '@api/medicalOrganizationsAddresses.api';

import useStateWithDebounce from '@hooks/useStateWithDebouce';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import UserPermissionGuard from '../guards/UserPermissionGuard';
import UpsertMedicalOrganizationAddressForm, {
  UPSERT_MEDICAL_ORGANIZATION_ADDRESS_FORM_MODAL_ID,
} from './UpsertMedicalOrganizationAddressForm';

interface MedicalOrganizationAddressesTableProps {
  medicalOrganization: MedicalOrganization;
}

export default function MedicalOrganizationAddressesTable({
  medicalOrganization,
}: MedicalOrganizationAddressesTableProps) {
  // ==========================================================================
  // General
  // ==========================================================================
  const canCreateMedicalOrganizationsAddresses =
    useUserPermissionGuard('ADDRESSES.CREATE');
  const canEditMedicalOrganizationsAddresses =
    useUserPermissionGuard('ADDRESSES.UPDATE');
  const canDeleteMedicalOrganizationsAddresses =
    useUserPermissionGuard('ADDRESSES.DELETE');

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] =
    useState<GetMedicalOrganizationsAddressesRequestParams>({
      page: +1,
      pageLength: +10,
      sortBy: 'id',
      sortOrder: 'asc',
    });

  const [nameSearch, setNameSearch, nameSearchDebounced] =
    useStateWithDebounce('');
  const [
    streetAddress1Search,
    setStreetAddress1Search,
    streetAddress1SearchDebounced,
  ] = useStateWithDebounce('');
  const [localitySearch, setLocalitySearch, localitySearchDebounced] =
    useStateWithDebounce('');
  const [countryCodeSearch, setCountryCodeSearch, countryCodeSearchDebounced] =
    useStateWithDebounce('');

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<MedicalOrganizationAddress>
  >({
    columnAccessor: 'id',
    direction: 'asc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as MedicalOrganizationsAddressesSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const {
    data: medicalOrganizationsAddressesCount = { count: 0 },
    isLoading: isLoadingCount,
  } = useCountMedicalOrganizationsAddressesQuery(
    {
      medicalOrganizationId: medicalOrganization.id,
      params: {
        nameSearch: nameSearchDebounced,
        streetAddress1Search: streetAddress1SearchDebounced,
        localitySearch: localitySearchDebounced,
        countryCodeSearch: countryCodeSearchDebounced,
      },
    },
    { skip: !medicalOrganization },
  );

  const { data = [], isLoading } = useGetManyMedicalOrganizationsAddressesQuery(
    {
      medicalOrganizationId: medicalOrganization.id,
      params: {
        nameSearch: nameSearchDebounced,
        streetAddress1Search: streetAddress1SearchDebounced,
        localitySearch: localitySearchDebounced,
        countryCodeSearch: countryCodeSearchDebounced,
      },
    },
    { skip: !medicalOrganization },
  );

  const [
    deleteMedicalOrganizationAddress,
    { isLoading: isLoadingDeleteMedicalOrganizationAddress },
  ] = useDeleteMedicalOrganizationAddressMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleAddMedicalOrganizationAddressClick = useCallback(() => {
    openModal({
      modalId: UPSERT_MEDICAL_ORGANIZATION_ADDRESS_FORM_MODAL_ID,
      title: 'Aggiungi indirizzo organizzazione medica',
      children: (
        <UpsertMedicalOrganizationAddressForm
          medicalOrganizationId={medicalOrganization.id}
        />
      ),
    });
  }, []);

  const handleEditMedicalOrganizationAddressClick = useCallback(
    (medicalOrganizationAddress: MedicalOrganizationAddress) => {
      openModal({
        modalId: UPSERT_MEDICAL_ORGANIZATION_ADDRESS_FORM_MODAL_ID,
        title: 'Modifica indirizzo organizzazione medica',
        children: (
          <UpsertMedicalOrganizationAddressForm
            medicalOrganizationId={medicalOrganization.id}
            medicalOrganizationAddress={medicalOrganizationAddress}
          />
        ),
      });
    },
    [],
  );

  const handleDeleteMedicalOrganizationAddressClick = useCallback(
    (id: string) => {
      openConfirmModal({
        title: 'Elimina indirizzo organizzazione medica',
        children:
          "Stai per eliminare l'indirizzo organizzazione medica. L'operazione non è reversibile.\nContinuare?",
        confirmProps: { color: 'red' },
        labels: { confirm: 'Conferma', cancel: 'Annulla' },
        onConfirm: async () => {
          try {
            await deleteMedicalOrganizationAddress(id).unwrap();

            showNotification({
              title: 'Indirizzo organizzazione medica eliminato',
              message: 'Indirizzo organizzazione medica eliminato con successo',
            });
          } catch (e) {
            if (isApiError(e)) {
              showNotification({
                title: 'Errore',
                message: e.data.message,
                color: 'red',
                icon: <IconX />,
              });
            } else {
              showNotification({
                title: 'Errore',
                message: 'Errore sconosciuto',
                color: 'red',
                icon: <IconX />,
              });
              console.error(e);
            }
          }
        },
      });
    },
    [],
  );

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <>
      <Group justify="space-between" mb="lg">
        <Group gap="xs">
          <IconMapPins />
          <Title order={3}>Indirizzi</Title>
        </Group>
        {canCreateMedicalOrganizationsAddresses && (
          <Button
            leftSection={<IconPlus />}
            onClick={handleAddMedicalOrganizationAddressClick}
          >
            Aggiungi indirizzo
          </Button>
        )}
      </Group>
      <UserPermissionGuard permission="ADDRESSES.READ">
        <DataTable
          /* General */
          striped
          noRecordsText="Nessun indirizzo trovato"
          minHeight={data.length === 0 ? 200 : undefined}
          /* Data */
          fetching={isLoading || isLoadingCount}
          columns={[
            {
              accessor: 'name',
              title: 'Nome',
              sortable: true,
              filter: (
                <TextInput
                  placeholder="Ricerca nome"
                  leftSection={<IconSearch size={16} />}
                  rightSection={
                    nameSearch.trim() !== '' ? (
                      <ActionIcon
                        size="sm"
                        variant="transparent"
                        c="dimmed"
                        onClick={() => setNameSearch('')}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    ) : undefined
                  }
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.currentTarget.value)}
                />
              ),
            },
            {
              accessor: 'countryCode',
              title: 'Nazione',
              sortable: true,
              filter: (
                <TextInput
                  placeholder="Ricerca nazione"
                  leftSection={<IconSearch size={16} />}
                  rightSection={
                    countryCodeSearch.trim() !== '' ? (
                      <ActionIcon
                        size="sm"
                        variant="transparent"
                        c="dimmed"
                        onClick={() => setCountryCodeSearch('')}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    ) : undefined
                  }
                  value={countryCodeSearch}
                  onChange={(e) => setCountryCodeSearch(e.currentTarget.value)}
                />
              ),
            },
            {
              accessor: 'stateProvince',
              title: 'Provincia',
              render: (record) => record.stateProvince || '-',
            },
            {
              accessor: 'locality',
              title: 'Località',
              sortable: true,
              filter: (
                <TextInput
                  placeholder="Ricerca località"
                  leftSection={<IconSearch size={16} />}
                  rightSection={
                    localitySearch.trim() !== '' ? (
                      <ActionIcon
                        size="sm"
                        variant="transparent"
                        c="dimmed"
                        onClick={() => setLocalitySearch('')}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    ) : undefined
                  }
                  value={localitySearch}
                  onChange={(e) => setLocalitySearch(e.currentTarget.value)}
                />
              ),
            },
            {
              accessor: 'postalCode',
              title: 'CAP',
            },
            {
              accessor: 'streetAddress1',
              title: 'Indirizzo 1',
              filter: (
                <TextInput
                  placeholder="Ricerca indirizzo"
                  leftSection={<IconSearch size={16} />}
                  rightSection={
                    streetAddress1Search.trim() !== '' ? (
                      <ActionIcon
                        size="sm"
                        variant="transparent"
                        c="dimmed"
                        onClick={() => setStreetAddress1Search('')}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    ) : undefined
                  }
                  value={streetAddress1Search}
                  onChange={(e) =>
                    setStreetAddress1Search(e.currentTarget.value)
                  }
                />
              ),
            },
            {
              accessor: 'streetAddress2',
              title: 'Indirizzo 2',
              render: (record) => record.streetAddress2 || '-',
            },
            {
              accessor: 'phone',
              title: 'Telefono',
              render: (record) => record.phone || '-',
            },
            {
              accessor: 'email',
              title: 'Email',
              render: (record) => record.email || '-',
            },
            {
              accessor: 'actions',
              title: '',
              textAlign: 'right',
              width: '0%',
              render: (record) => (
                <Group wrap="nowrap" gap="xs">
                  {canEditMedicalOrganizationsAddresses && (
                    <Tooltip label="Modifica indirizzo organizzazione medica">
                      <ActionIcon
                        variant="subtle"
                        onClick={() =>
                          handleEditMedicalOrganizationAddressClick(record)
                        }
                      >
                        <IconPencil />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {canDeleteMedicalOrganizationsAddresses && (
                    <Tooltip label="Elimina indirizzo organizzazione medica">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        disabled={isLoadingDeleteMedicalOrganizationAddress}
                        onClick={() =>
                          handleDeleteMedicalOrganizationAddressClick(record.id)
                        }
                      >
                        <IconTrash />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              ),
            },
          ]}
          records={data}
          /* Sorting */
          sortStatus={sortStatus}
          onSortStatusChange={setSortStatus}
          /* Pagination */
          page={filters.page!}
          recordsPerPage={filters.pageLength!}
          totalRecords={medicalOrganizationsAddressesCount.count}
          paginationText={() => ''}
          recordsPerPageLabel=""
          recordsPerPageOptions={[10, 20, 50]}
          onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
          onRecordsPerPageChange={(newPageLength) =>
            setFilters({ ...filters, pageLength: newPageLength, page: 1 })
          }
        />
      </UserPermissionGuard>
    </>
  );
}
